import React, { useState } from 'react';
import paymentService from '../../services/payment.service';
import PaymentSuccessModal from './PaymentSuccessModal';

/**
 * Razorpay Checkout Component
 * Handles the Razorpay payment flow with success modal
 */
const RazorpayCheckout = ({ 
  paymentType,      // 'credit' or 'bundle'
  communicationType, // 'email' or 'sms' (for credit purchases)
  credits,          // Number of credits (for credit purchases)
  bundleId,         // Bundle ID (for bundle purchases)
  onSuccess,        // Callback on success
  onFailure,        // Callback on failure
  onClose,          // Callback when modal is closed
  children          // Trigger button/component
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [paymentId, setPaymentId] = useState(null);
  const [, setPurchaseData] = useState(null);

  // Load Razorpay script
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // Handle payment initiation
  const handlePayment = async () => {
    setLoading(true);
    setError(null);

    try {
      // Step 1: Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setError('Failed to load Razorpay. Please check your internet connection.');
        setLoading(false);
        return;
      }

      // Step 2: Create order on backend
      const orderData = { type: paymentType };
      if (paymentType === 'credit') {
        orderData.communicationType = communicationType;
        orderData.credits = credits;
      } else if (paymentType === 'bundle') {
        orderData.bundleId = bundleId;
      }

      const orderResponse = await paymentService.createOrder(orderData);
      
      if (!orderResponse.success) {
        setError(orderResponse.message || 'Failed to create payment order');
        setLoading(false);
        if (onFailure) onFailure(orderResponse);
        return;
      }

      const { orderId, amount, currency, keyId, paymentId: orderPaymentId, priceBreakdown, purchaseDetails } = orderResponse.data;
      
      // Store for success modal
      setPaymentId(orderPaymentId);
      setPurchaseData({ purchaseDetails, priceBreakdown });

      // Step 3: Configure Razorpay options
      const options = {
        key: keyId,
        amount: Math.round(amount * 100),
        currency: currency || 'INR',
        name: 'Truyon',
        description: paymentType === 'credit' 
          ? `Purchase ${credits} ${communicationType} credits` 
          : `Purchase Bundle`,
        order_id: orderId,
        handler: async (response) => {
          // Step 4: Handle payment success
          setLoading(true);
          try {
            const verifyResponse = await paymentService.verifyPayment({
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
              paymentId: orderPaymentId
            });

            if (verifyResponse.success) {
              // Show success modal instead of navigating
              setShowSuccessModal(true);
              if (onSuccess) onSuccess(verifyResponse.data);
            } else {
              setError(verifyResponse.message || 'Payment verification failed');
              if (onFailure) onFailure(verifyResponse);
            }
          } catch (error) {
            setError(error.message || 'Payment verification failed');
            if (onFailure) onFailure(error);
          } finally {
            setLoading(false);
          }
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            if (onClose) onClose();
          },
        },
        prefill: {
          name: '',
          email: '',
          contact: '',
        },
        notes: {
          paymentType: paymentType,
          tenantId: localStorage.getItem('tenantId') || '',
        },
        theme: {
          color: '#4F46E5',
        },
      };

      // Step 5: Open Razorpay checkout
      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (error) {
      console.error('Payment error:', error);
      setError(error.message || 'Payment failed. Please try again.');
      if (onFailure) onFailure(error);
      setLoading(false);
    }
  };

  // Handle success modal close
  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    if (onClose) onClose();
  };

  return (
    <>
      {/* Trigger Button */}
      {React.cloneElement(children, {
        onClick: handlePayment,
        disabled: loading || children.props.disabled,
        children: loading ? 'Processing...' : children.props.children,
      })}
      
      {/* Error Display */}
      {error && (
        <div className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded-lg">
          {error}
        </div>
      )}

      {/* Success Modal */}
      <PaymentSuccessModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        paymentId={paymentId}
        onNavigate={(path) => {
          // Handle navigation after modal close
          window.location.href = path;
        }}
      />
    </>
  );
};

export default RazorpayCheckout;